import { useQuery } from '@tanstack/react-query'
import { PlanKey, Pricing, PricingPlan, pricingApi } from '../api/client'

// Единственный источник цен — backend/app/tariffs.py, отдаётся через
// GET /api/subscription/pricing. Здесь намеренно НЕТ запасных чисел:
// пока цена не приехала, витрина показывает скелетон. Захардкоженная
// «на всякий случай» копия — ровно то, из-за чего цена и разъехалась
// (в уведомлении 1000 Stars, в ЮKassa 999 ₽, в Mini App 1290).

/** 1290 → '1 290' */
export function fmtNum(n: number): string {
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

export function fmtStars(n: number): string {
  return `${fmtNum(n)} Stars`
}

export function fmtRub(n: number): string {
  return `${fmtNum(n)}₽`
}

/** Помесячный эквивалент годовой цены — то же округление, что на бэкенде.
 *
 * Python round() округляет половину к чётному, а Math.round() — вверх, поэтому
 * ровно на .5 витрины расходились: бот показывал 3 990 ₽/год как 332 ₽/мес,
 * а Mini App — как 333. Повторяем правило Python, чтобы число совпадало
 * с tariffs.per_month() на всех поверхностях.
 */
export function perMonth(n: number): number {
  const value = n / 12
  const floor = Math.floor(value)
  const rest = value - floor
  if (rest > 0.5) return floor + 1
  if (rest < 0.5) return floor
  return floor % 2 === 0 ? floor : floor + 1
}

export interface PricingHelpers {
  isLoading: boolean
  isError: boolean
  /** План по ключу; undefined, пока цены не загружены. */
  plan: (key: PlanKey) => PricingPlan | undefined
  /** '−17%' — берётся с бэкенда, а не считается на глаз. */
  yearlyDiscountLabel: string
  refetch: () => void
}

export function usePricing(): PricingHelpers {
  const { data, isLoading, isError, refetch } = useQuery<Pricing>({
    queryKey: ['pricing'],
    queryFn: () => pricingApi.get(),
    // Цена меняется деплоем, а не в течение сессии.
    staleTime: 30 * 60_000,
  })

  const byKey = new Map<PlanKey, PricingPlan>(
    (data?.plans ?? []).map(p => [p.key, p]),
  )

  return {
    isLoading,
    isError,
    plan: (key: PlanKey) => byKey.get(key),
    yearlyDiscountLabel: data ? `−${data.yearly_discount_percent}%` : '',
    refetch: () => { refetch() },
  }
}
